import i18n from "@/plugins/i18n";

interface MonthNames {
  long: string
  short: string
  shortDot: string
}

interface ParsedDate {
  year?: number
  month?: number   // 1-12
  day?: number
}

type DateInput = string | Date | null | undefined;

const USER_TIME_ZONE =
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Berlin';

const MONTHS: Record<string, MonthNames[]> = {
  en: [
    { long: 'january', short: 'jan', shortDot: 'jan.' },
    { long: 'february', short: 'feb', shortDot: 'feb.' },
    { long: 'march', short: 'mar', shortDot: 'mar.' },
    { long: 'april', short: 'apr', shortDot: 'apr.' },
    { long: 'may', short: 'may', shortDot: 'may.' },
    { long: 'june', short: 'jun', shortDot: 'jun.' },
    { long: 'july', short: 'jul', shortDot: 'jul.' },
    { long: 'august', short: 'aug', shortDot: 'aug.' },
    { long: 'september', short: 'sep', shortDot: 'sep.' },
    { long: 'october', short: 'oct', shortDot: 'oct.' },
    { long: 'november', short: 'nov', shortDot: 'nov.' },
    { long: 'december', short: 'dec', shortDot: 'dec.' },
  ],
  de: [
    { long: 'januar', short: 'jan', shortDot: 'jan.' },
    { long: 'februar', short: 'feb', shortDot: 'feb.' },
    { long: 'märz', short: 'märz', shortDot: 'märz.' },
    { long: 'april', short: 'apr', shortDot: 'apr.' },
    { long: 'mai', short: 'mai', shortDot: 'mai.' },
    { long: 'juni', short: 'jun', shortDot: 'jun.' },
    { long: 'juli', short: 'jul', shortDot: 'jul.' },
    { long: 'august', short: 'aug', shortDot: 'aug.' },
    { long: 'september', short: 'sep', shortDot: 'sep.' },
    { long: 'oktober', short: 'okt', shortDot: 'okt.' },
    { long: 'november', short: 'nov', shortDot: 'nov.' },
    { long: 'dezember', short: 'dez', shortDot: 'dez.' },
  ],
}

const MONTH_LOOKUP = new Map<string, number>()

export const PickerView = {
  Year: 'year',
  Months: 'months',
  Date: 'date',
} as const

for (const locale of ['en', 'de']) {
  for (let i = 0; i < 12; i++) {
    const m = MONTHS[locale]?.[i]
    if (m) {
      MONTH_LOOKUP.set(m.long, i + 1)
      MONTH_LOOKUP.set(m.short, i + 1)
      MONTH_LOOKUP.set(m.shortDot, i + 1)
    }
  }
}

// Safe because only backend dates are indexed.
const tokenCache = new Map<string, string[]>()

function getMonthNumber(text: string): number | null {
  return MONTH_LOOKUP.get(text) ?? null
}

export function normalizeDate(value: any, withTime: boolean = false): string | null {
  if (!value) return null;

  // ISO
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  // dd.MM.yyyy
  if (typeof value === 'string' && /^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
    const [day, month, year] = value.split(".");
    return `${year}-${month}-${day}`;
  }

  // ISO with time
  if (typeof value === 'string' && value.includes('T')) {
    const [date, timeRaw] = value.split('T');
    const time = timeRaw.split('.')[0];
    return withTime ? getLocalTime(`${date} ${time}`) : date;
  }

  if (value instanceof Date) {
    const pad = (n: number) => String(n).padStart(2, '0');

    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }

  console.error('normalizeDate: INVALID DATE FORMAT', value);
  return null;
}

export function formatDate(d: Date | string, withTime = false): string {
  if (!d) return "";

  const pad = (n: number) => String(n).padStart(2, "0");
  const date = typeof d === "string" ? new Date(d) : d;

  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();

  if (!withTime || (date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0)) {
    return `${day}.${month}.${year}`;
  }

  return `${day}.${month}.${year} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export function sortDate(a: string, b: string): number {
  const ta = toEpoch(a)
  const tb = toEpoch(b)

  if (ta === null && tb === null) return 0
  if (ta === null) return 1
  if (tb === null) return -1
  return ta - tb
}

function toEpoch(s: unknown): number | null {
  if (!s || typeof s !== 'string') return null;
  const t = s.trim().replace(' ', 'T');
  const withZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(t) ? t : `${t}Z`;
  const d = new Date(withZone);
  return isNaN(d.getTime()) ? null : d.getTime();
}

export function getLocalTime(utcDate: string | Date): string {
  const date = parseInstant(utcDate);

  if (!date) return '';

  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: USER_TIME_ZONE,
  }).format(date)
    .replace(',', '');
}

export function formatDisplayDate(value: string): string {
  if (!value) return ""

  if (/[A-Za-z]/.test(value)) return value

  let iso: string | null = null

  // Try to parse ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    iso = value
  }

  // Try to parse dd.mm.yyyy
  else if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
    const [d, m, y] = value.split('.')
    iso = `${y}-${m}-${d}`
  }

  if (!iso) return value

  const [year, month, day] = iso.split('-')
  const monthIndex = parseInt(month, 10) - 1

  const lang = (i18n.global.locale as unknown as { value: string }).value || (typeof navigator !== 'undefined' ? navigator.language : 'en')
  const formatter = new Intl.DateTimeFormat(lang, { month: 'short' })
  const monthName = formatter.format(new Date(parseInt(year), monthIndex, 1))

  return `${parseInt(day, 10)} ${monthName}. ${year}`
}

export function toDisplayDate(iso: string | null): string | null {
  if (!iso) return null

  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso

  const [y, m, d] = iso.split('-')
  return `${d}.${m}.${y}`
}

export function toIsoDate(display: string | null): string | null {
  if (!display) return null

  if (/^\d{4}-\d{2}-\d{2}$/.test(display)) return display

  if (/^\d{2}\.\d{2}\.\d{4}$/.test(display)) {
    const [d, m, y] = display.split('.')
    return `${y}-${m}-${d}`
  }

  return null
}

export function dateToIso(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function parseDisplayDateInput(value: string): string | null {
  if (!value) return null;

  const digits = value.replace(/\D/g, "").slice(0, 8);

  if (digits.length < 6) return null;

  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);

  let yearRaw = digits.slice(4);

  if (yearRaw.length === 2) {
    const yearNum = Number(yearRaw);

    yearRaw = yearNum <= 50
      ? `20${yearRaw}`
      : `19${yearRaw}`;
  }

  if (yearRaw.length !== 4) return null;

  const year = yearRaw;

  const date = new Date(Number(year), Number(month) - 1, Number(day));

  const valid =
    date.getFullYear() === Number(year) &&
    date.getMonth() === Number(month) - 1 &&
    date.getDate() === Number(day);

  if (!valid) return null;

  return `${year}-${month}-${day}`;
}

function getAllMonthTexts(monthIndex: number): string[] {
  const result: string[] = []
  for (const locale of ['en', 'de']) {
    const m = MONTHS[locale]?.[monthIndex]
    if (m) {
      result.push(m.long, m.short, m.shortDot)
    }
  }

  return [...new Set(result)]
}

function expandTwoDigitYear(value: string): number | undefined {
  if (!/^\d{2}$/.test(value)) {
    return undefined
  }

  const year = Number(value)

  return year < 50
    ? 2000 + year
    : 1900 + year
}

function parseIsoDate(input: string): ParsedDate | null {
  const trimmed = input.trim()

  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (match) {
    const year = parseInt(match[1], 10)
    const month = parseInt(match[2], 10)
    const day = parseInt(match[3], 10)
    if (year > 0 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return { year, month, day }
    }
  }
  return null
}

function parseDisplayDate(input: string): ParsedDate | null {
  const trimmed = input.trim()
  const parts = trimmed.split(/[.\/ ]/).filter(p => p.length > 0)
  if (parts.length < 3) return null

  let day = parts[0]
  let month = parts[1]
  let year = parts[2]

  if (day.length === 1) day = '0' + day
  if (month.length === 1) month = '0' + month

  if (year.length === 2) {
    const expandedYear = expandTwoDigitYear(year)

    if (expandedYear === undefined) {
      return null
    }

    year = String(expandedYear)
  }

  if (year.length !== 4) return null

  const y = parseInt(year, 10)
  const m = parseInt(month, 10)
  const d = parseInt(day, 10)

  if (!isValidDateParts(y, m, d)) return null

  return { year: y, month: m, day: d }
}

function parseTextDate(input: string): ParsedDate | null {
  let normalized = input.replace(/\./g, ' ').replace(/\s+/g, ' ').trim()
  if (!normalized.includes(' ')) {
    normalized = normalized.replace(/(\d)([a-zA-Z])/g, '$1 $2')
    normalized = normalized.replace(/([a-zA-Z])(\d)/g, '$1 $2')
  }

  const parts = normalized.split(' ')
  let dayPart: string | null = null
  let monthPart: string | null = null
  let yearPart: string | null = null

  for (const p of parts) {
    if (/^\d+$/.test(p)) {
      const num = parseInt(p, 10)
      if (num >= 1 && num <= 31) {
        if (dayPart === null) dayPart = p
        else if (yearPart === null) yearPart = p
      } else if (num >= 1000 && num <= 9999) {
        yearPart = p
      } else if (num >= 0 && num <= 99) {
        yearPart = p
      }
    } else if (/[a-zA-Z]/.test(p)) {
      monthPart = p.toLowerCase()
    }
  }

  if (!monthPart) return null

  const monthNumber = getMonthNumber(monthPart)
  if (!monthNumber) return null

  let yearNumber: number | undefined
  if (yearPart) {
    const y = parseInt(yearPart, 10)
    if (y >= 1000 && y <= 9999) {
      yearNumber = y
    } else if (y >= 0 && y <= 99) {
      yearNumber = expandTwoDigitYear(yearPart)
    } else {
      return null
    }
  }

  let dayNumber: number | undefined
  if (dayPart) {
    const d = parseInt(dayPart, 10)
    if (d < 1 || d > 31) return null
    dayNumber = d
  }

  if (dayNumber !== undefined && yearNumber !== undefined) {
    if (!isValidDateParts(yearNumber, monthNumber, dayNumber)) return null
  }

  return {
    year: yearNumber,
    month: monthNumber,
    day: dayNumber,
  }
}

function parseDayOnly(input: string): ParsedDate | null {
  const trimmed = input.trim()
  const match = trimmed.match(/^(\d{1,2})[.\s]*$/)
  if (match) {
    const day = parseInt(match[1], 10)
    if (day >= 1 && day <= 31) {
      return { day }
    }
  }
  return null
}

/**
 * Parse a user date string input into a ParsedDate object.
 * @param input
 */
export function parseDateString(input: string): ParsedDate | null {
  if (!input) return null

  const dayOnly = parseDayOnly(input)
  if (dayOnly) return dayOnly

  const isoFromOldParser = parseDisplayDateInput(input)
  if (isoFromOldParser) {
    const parts = isoFromOldParser.split('-')
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10)
      const day = parseInt(parts[2], 10)
      if (isValidDateParts(year, month, day)) {
        return { year, month, day }
      }
    }
  }

  let parsed = parseIsoDate(input)
  if (parsed) return parsed

  parsed = parseDisplayDate(input)
  if (parsed) return parsed

  parsed = parseTextDate(input)
  if (parsed) return parsed

  return null
}

function addFullDate(
  dVariants: string[],
  mNumerics: string[],
  mTexts: string[],
  yVariants: string[],
  separators: string[],
  results: string[]
): void {
  for (const d of dVariants) {
    for (const m of mNumerics) {
      for (const y of yVariants) {
        for (const sep1 of separators) {
          for (const sep2 of separators) {
            const parts = []
            parts.push(d)
            if (sep1) parts.push(sep1)
            parts.push(m)
            if (sep2) parts.push(sep2)
            parts.push(y)
            results.push(parts.join(''))
          }
        }
      }
    }

    for (const mText of mTexts) {
      for (const y of yVariants) {
        for (const sep1 of separators) {
          for (const sep2 of separators) {
            const parts = []
            parts.push(d)
            if (sep1) parts.push(sep1)
            parts.push(mText)
            if (sep2) parts.push(sep2)
            parts.push(y)
            results.push(parts.join(''))
          }
        }
      }
    }
  }
}

function addDayMonth(
  dVariants: string[],
  mNumerics: string[],
  mTexts: string[],
  separators: string[],
  results: string[]
): void {
  for (const d of dVariants) {
    for (const m of mNumerics) {
      for (const sep of separators) {
        const parts = []
        parts.push(d)
        if (sep) parts.push(sep)
        parts.push(m)
        results.push(parts.join(''))
      }
    }
    for (const mText of mTexts) {
      for (const sep of separators) {
        const parts = []
        parts.push(d)
        if (sep) parts.push(sep)
        parts.push(mText)
        results.push(parts.join(''))
      }
    }
  }
}

function addMonthYear(
  mNumerics: string[],
  mTexts: string[],
  yVariants: string[],
  separators: string[],
  results: string[]
): void {
  for (const m of mNumerics) {
    for (const y of yVariants) {
      for (const sep of separators) {
        const parts = []
        parts.push(m)
        if (sep) parts.push(sep)
        parts.push(y)
        results.push(parts.join(''))
      }
    }
  }
  for (const mText of mTexts) {
    for (const y of yVariants) {
      for (const sep of separators) {
        const parts = []
        parts.push(mText)
        if (sep) parts.push(sep)
        parts.push(y)
        results.push(parts.join(''))
      }
    }
  }
}

function addComponents(
  dVariants: string[],
  mNumerics: string[],
  mTexts: string[],
  yVariants: string[],
  results: string[]
): void {
  results.push(...dVariants, ...mNumerics, ...mTexts, ...yVariants)
  for (const d of dVariants) {
    results.push(`${d}.`, `${d} `)
  }
  for (const m of mNumerics) {
    results.push(`${m}.`, `${m} `)
  }
}

export function buildDateSearchTokens(parsed: ParsedDate): string[] {
  const { year, month, day } = parsed
  const results: string[] = []

  const yearStr = year ? String(year) : ''
  const yy = yearStr.slice(2)
  const monthStr = month ? String(month).padStart(2, '0') : ''
  const monthNum = month ? String(month) : ''
  const dayStr = day ? String(day).padStart(2, '0') : ''
  const dayNum = day ? String(day) : ''

  const dVariants = day ? [dayStr, dayNum] : []
  const mNumerics = month ? [monthStr, monthNum] : []
  const yVariants = year ? [yearStr, yy] : []

  const monthTexts = month ? getAllMonthTexts(month - 1) : []

  const separators = ['.', ' ', '']

  addFullDate(dVariants, mNumerics, monthTexts, yVariants, separators, results)
  addDayMonth(dVariants, mNumerics, monthTexts, separators, results)
  addMonthYear(mNumerics, monthTexts, yVariants, separators, results)
  addComponents(dVariants, mNumerics, monthTexts, yVariants, results)

  if (yearStr && monthStr && dayStr) {
    results.push(`${yearStr}-${monthStr}-${dayStr}`)
    results.push(`${dayStr}.${monthStr}.${yearStr}`)
  }

  return [...new Set(results.filter(Boolean))]
}

export function getDateSearchStrings(dateInput: string): string[] {
  if (!dateInput) return []

  const key = dateInput.trim().toLowerCase()
  const cached = tokenCache.get(key)
  if (cached) return cached

  const parsed = parseDateString(dateInput)

  if (!parsed) {
    const result = [dateInput.toLowerCase()]
    tokenCache.set(key, result)
    return result
  }

  const tokens = buildDateSearchTokens(parsed)

  tokens.push(dateInput.toLowerCase())

  const result = [...new Set(tokens.filter(Boolean))]
  tokenCache.set(key, result)
  return result
}

export function isValidDateParts(
  year:number,
  month:number,
  day:number
): boolean {

  if (year < 1 || year > 9999) return false
  if (month < 1 || month > 12) return false
  if (day < 1 || day > 31) return false

  const date = new Date(year, month - 1, day)

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  )
}

function parseInstant(value: DateInput): Date | null {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? null
      : new Date(value.getTime());
  }

  const input = value.trim();

  if (!input) return null;

  // Explicit timezone: Z or +02:00
  if (
    /Z$/i.test(input) ||
    /[+-]\d{2}:?\d{2}$/.test(input)
  ) {
    const date = new Date(input);

    return Number.isNaN(date.getTime())
      ? null
      : date;
  }

  // Legacy backend format:
  // "2023-05-05 15:25:27"
  const utcMatch = input.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/
  );

  if (utcMatch) {
    const [, year, month, day, hour, minute, second = '0'] = utcMatch;

    const date = new Date(
      Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second)
      )
    );

    return Number.isNaN(date.getTime())
      ? null
      : date;
  }

  return null;
}

function toCalendarDateKey(value: unknown): number | null {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  const parsed = parseDateString(value);

  if (
    !parsed ||
    parsed.year === undefined ||
    parsed.month === undefined ||
    parsed.day === undefined
  ) {
    return null;
  }

  return Date.UTC(
    parsed.year,
    parsed.month - 1,
    parsed.day
  );
}

export function sortCalendarDate(
  a: string,
  b: string
): number {
  const ta = toCalendarDateKey(a);
  const tb = toCalendarDateKey(b);

  if (ta === null && tb === null) return 0;
  if (ta === null) return 1;
  if (tb === null) return -1;

  return ta - tb;
}

export function useDateHelper() {
  return { sortDate, sortCalendarDate, getLocalTime, formatDisplayDate };
}
